import { Sheet, Settings } from '../types';

// データベース名とストア名の定義
const DB_NAME = 'pouches_db';
const DB_VERSION = 1;
const SHEET_STORE = 'sheets';
const SETTINGS_STORE = 'settings';

// IndexedDBの初期化
export const initDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => {
      reject(new Error('データベースを開けませんでした'));
    };
    
    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // シートストアの作成
      if (!db.objectStoreNames.contains(SHEET_STORE)) {
        const sheetStore = db.createObjectStore(SHEET_STORE, { keyPath: 'id' });
        sheetStore.createIndex('createdAt', 'createdAt', { unique: false });
      }
      
      // 設定ストアの作成
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: 'id' });
      }
    };
  });
};

// データベースの接続取得
const getDbConnection = async (): Promise<IDBDatabase> => {
  return await initDatabase();
};

// シート関連のメソッド
export const SheetStorage = {
  // 全シートの取得
  getAllSheets: async (): Promise<Sheet[]> => {
    const db = await getDbConnection();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SHEET_STORE, 'readonly');
      const store = transaction.objectStore(SHEET_STORE);
      const request = store.getAll();
      
      request.onsuccess = () => {
        // 日付文字列をDate型に変換
        const sheets = request.result.map((sheet: any) => {
          const convertedSheet = {
            ...sheet,
            createdAt: new Date(sheet.createdAt),
            lastModified: sheet.lastModified ? new Date(sheet.lastModified) : undefined,
            history: sheet.history ? sheet.history.map((entry: any) => ({
              ...entry,
              timestamp: new Date(entry.timestamp)
            })) : [],
            snapshots: sheet.snapshots ? sheet.snapshots.map((snapshot: any) => ({
              ...snapshot,
              timestamp: new Date(snapshot.timestamp)
            })) : []
          };
          
          // historyIndexに対応する履歴エントリが存在する場合、その状態を使用する
          if (convertedSheet.history && convertedSheet.history.length > 0 && 
              convertedSheet.historyIndex >= 0 && convertedSheet.historyIndex < convertedSheet.history.length) {
            // 履歴エントリから状態を取得
            const historyEntry = convertedSheet.history[convertedSheet.historyIndex];
            // シートのpouchesとgroupsを履歴エントリの状態で更新
            convertedSheet.pouches = historyEntry.pouches;
            convertedSheet.groups = historyEntry.groups;
          }
          
          return convertedSheet;
        });
        
        // 作成日時でソート
        sheets.sort((a: Sheet, b: Sheet) => b.createdAt.getTime() - a.createdAt.getTime());
        
        
        resolve(sheets);
      };
      
      request.onerror = () => {
        reject(new Error('シートの取得に失敗しました'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  },
  
  // シートの保存
  saveSheet: async (sheet: Sheet): Promise<void> => {
    // 移動中のパウチをログに出力
    const movedPouches = sheet.pouches.filter(p => (p as any)._isMoving);
    
    const db = await getDbConnection();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SHEET_STORE, 'readwrite');
      const store = transaction.objectStore(SHEET_STORE);
      
      // 最終更新日時を更新し、一時的なフラグを除外
      const updatedSheet = {
        ...sheet,
        lastModified: new Date(),
        pouches: sheet.pouches.map(pouch => {
          // 一時的なUIフラグを除外
          const pouchAny = pouch as any;
          const { 
            _isDragging, _isCloneSource, _isCloning, _isFromOtherSheet,
            _isMoving, _isDeleting, _bounceBack, _isCrossSheetDrag, _animateSubtotal,
            _isInMuteGroup, _isAdding, _previousSubtotal, _animateFromZero,
            _animateSignChange, _animateMultiplier, _noTransition,
            ...cleanPouch 
          } = pouchAny;
          return cleanPouch;
        }),
        groups: sheet.groups.map(group => {
          // 一時的なUIフラグを除外
          const groupAny = group as any;
          const {
            _isDragging, _isCloning, _animateSubtotal, _isDeleting,
            ...cleanGroup
          } = groupAny;
          return cleanGroup;
        })
      };

      
      const request = store.put(updatedSheet);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        console.error(`[DEBUG] SheetStorage.saveSheet: DB保存失敗`, request.error);
        reject(new Error('シートの保存に失敗しました'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  },
  
  // 特定のシートの取得
  getSheet: async (id: string): Promise<Sheet | null> => {
    const db = await getDbConnection();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SHEET_STORE, 'readonly');
      const store = transaction.objectStore(SHEET_STORE);
      const request = store.get(id);
      
      request.onsuccess = () => {
        if (request.result) {
          // 日付文字列をDate型に変換
          const sheet = {
            ...request.result,
            createdAt: new Date(request.result.createdAt),
            lastModified: request.result.lastModified ? new Date(request.result.lastModified) : undefined,
            history: request.result.history ? request.result.history.map((entry: any) => ({
              ...entry,
              timestamp: new Date(entry.timestamp)
            })) : [],
            snapshots: request.result.snapshots ? request.result.snapshots.map((snapshot: any) => ({
              ...snapshot,
              timestamp: new Date(snapshot.timestamp)
            })) : []
          };
          
          
          // historyIndexに対応する履歴エントリが存在する場合、その状態を使用する
          if (sheet.history && sheet.history.length > 0 && 
              sheet.historyIndex >= 0 && sheet.historyIndex < sheet.history.length) {
            // 履歴エントリから状態を取得
            const historyEntry = sheet.history[sheet.historyIndex];
            // シートのpouchesとgroupsを履歴エントリの状態で更新
            sheet.pouches = historyEntry.pouches;
            sheet.groups = historyEntry.groups;
          }
          
          
          resolve(sheet);
        } else {
          resolve(null);
        }
      };
      
      request.onerror = () => {
        reject(new Error('シートの取得に失敗しました'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  },
  
  // シートの削除
  deleteSheet: async (id: string): Promise<void> => {
    const db = await getDbConnection();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SHEET_STORE, 'readwrite');
      const store = transaction.objectStore(SHEET_STORE);
      const request = store.delete(id);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(new Error('シートの削除に失敗しました'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  },
  
  // アクティブなシートIDの保存と取得
  saveActiveSheetId: async (id: string): Promise<void> => {
    localStorage.setItem('pouches-active-sheet', id);
  },
  
  getActiveSheetId: (): string | null => {
    return localStorage.getItem('pouches-active-sheet');
  }
};

// 設定関連のメソッド
export const SettingsStorage = {
  // デフォルト設定
  DEFAULT_SETTINGS: {
    id: 'app-settings',
    buttonsVisible: true,
    posinegaVisible: true,
    defaultValue: 100,
    autoHideMenu: false,
    isLeftSidebarOpen: window.innerWidth > 768,
    isRightSidebarOpen: window.innerWidth > 768
  },
  
  // 設定の取得
  getSettings: async (): Promise<Settings> => {
    const db = await getDbConnection();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SETTINGS_STORE, 'readonly');
      const store = transaction.objectStore(SETTINGS_STORE);
      const request = store.get('app-settings');
      
      request.onsuccess = () => {
        if (request.result) {
          resolve(request.result);
        } else {
          // デフォルト設定を返す
          resolve(SettingsStorage.DEFAULT_SETTINGS);
        }
      };
      
      request.onerror = () => {
        reject(new Error('設定の取得に失敗しました'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  },
  
  // 設定の保存
  saveSettings: async (settings: Settings): Promise<void> => {
    const db = await getDbConnection();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(SETTINGS_STORE, 'readwrite');
      const store = transaction.objectStore(SETTINGS_STORE);
      
      // IDを確実に設定
      const settingsToSave = {
        ...settings,
        id: 'app-settings'
      };
      
      const request = store.put(settingsToSave);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(new Error('設定の保存に失敗しました'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  }
};

// データの初期化（初回起動時に呼び出す）
export const initializeData = async (): Promise<void> => {
  try {
    // データベースを初期化
    await initDatabase();
    
    // シートが一つもない場合は初期シートを作成
    const sheets = await SheetStorage.getAllSheets();
    if (sheets.length === 0) {
      // 初期シートの作成ロジックは別途実装
      
    }
    
    // 設定を初期化（設定がなければデフォルト設定が使用される）
    await SettingsStorage.getSettings();
  } catch (error) {
    console.error('データの初期化に失敗しました:', error);
    throw error;
  }
}; 